import { Link } from 'react-router-dom';

const useCases = [
  {
    industry: 'Contractors & Trades',
    tagline: 'Plumbers, HVAC, Electricians & More',
    description:
      'Win more local jobs with AI-powered ads that target homeowners in your area. Our AI ad generator knows how to write urgency-driven copy for emergency services, seasonal promotions, and new customer offers — without you spending hours writing.',
    bullets: [
      '"Emergency HVAC repair — same day service"',
      '"Get 3 quotes, we\'ll beat the best"',
      '"Book your spring plumbing inspection today"',
    ],
    image:
      'https://readdy.ai/api/search-image?query=professional%20male%20contractor%20in%20hard%20hat%20and%20work%20vest%20at%20a%20residential%20job%20site%2C%20tools%20and%20equipment%20visible%2C%20suburban%20home%20background%2C%20confident%20posture%2C%20warm%20sunny%20day%2C%20authentic%20realistic%20photography%2C%20natural%20earthy%20tones&width=640&height=420&seq=usecase-contractor-01&orientation=landscape',
    cta: 'Generate Contractor Ads',
  },
  {
    industry: 'Real Estate Agents',
    tagline: 'Listings, Open Houses & Lead Gen',
    description:
      'Create compelling listing ads, open house promotions, and buyer/seller lead generation campaigns in minutes. Each AI-generated ad is crafted to showcase your properties and build your local brand.',
    bullets: [
      '"3BR/2BA in Oak Park — Open House Sunday 2pm"',
      '"Thinking of selling? Get a free home valuation"',
      '"Buyers: See new listings before anyone else"',
    ],
    image:
      'https://readdy.ai/api/search-image?query=confident%20professional%20female%20real%20estate%20agent%20in%20business%20attire%20standing%20in%20front%20of%20a%20beautiful%20modern%20suburban%20home%2C%20golden%20hour%20lighting%2C%20welcoming%20smile%2C%20upscale%20neighborhood%2C%20premium%20photography%20style&width=640&height=420&seq=usecase-realestate-01&orientation=landscape',
    cta: 'Generate Real Estate Ads',
  },
  {
    industry: 'Local Businesses',
    tagline: 'Restaurants, Salons, Gyms & Retail',
    description:
      'Compete with big brands using the same AI ad technology. Drive foot traffic, fill appointment slots, and grow your customer base with high-quality ads that actually sound like your business — not a template.',
    bullets: [
      '"New members: First month free — join today"',
      '"Happy hour 4–7pm — $5 tacos & $3 drinks"',
      '"Book your blowout this weekend — slots filling fast"',
    ],
    image:
      'https://readdy.ai/api/search-image?query=busy%20vibrant%20local%20small%20business%20storefront%20with%20happy%20customers%2C%20warm%20inviting%20atmosphere%2C%20mix%20of%20restaurant%20gym%20and%20salon%20style%20elements%2C%20urban%20neighborhood%20setting%2C%20natural%20daylight%2C%20authentic%20candid%20community%20feel%2C%20rich%20warm%20tones&width=640&height=420&seq=usecase-localbiz-01&orientation=landscape',
    cta: 'Generate Local Business Ads',
  },
  {
    industry: 'E-Commerce & DTC Brands',
    tagline: 'Product Launches, Sales & Retargeting',
    description:
      'Scale your online store with product-specific ad copy and creative that drives ROAS. From launch campaigns to Black Friday sales to retargeting scripts — the AI ad generator handles every stage of the funnel.',
    bullets: [
      '"Introducing [Product]: 20% off launch week only"',
      '"You left something in your cart 👀 — here\'s 10% off"',
      '"5-star reviews don\'t lie — shop now before it sells out"',
    ],
    image:
      'https://readdy.ai/api/search-image?query=stylish%20ecommerce%20product%20flatlay%20with%20packaging%20boxes%20shipping%20materials%20and%20online%20shopping%20elements%20on%20a%20clean%20white%20marble%20surface%2C%20bright%20studio%20lighting%2C%20modern%20minimalist%20aesthetic%2C%20premium%20brand%20feel%2C%20sharp%20details&width=640&height=420&seq=usecase-ecom-01&orientation=landscape',
    cta: 'Generate E-Commerce Ads',
  },
];

export default function LandingUseCases() {
  return (
    <section id="use-cases" className="py-20 lg:py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">
            Made for Your Industry
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            The AI Ad Generator That Understands{' '}
            <span className="text-orange-500">Your Business</span>
          </h2>
          <p className="text-base text-stone-500 leading-relaxed">
            Whether you&apos;re a local tradesman or an online brand, our AI creates ads with the
            right tone, offers, and messaging for your industry — automatically.
          </p>
        </div>

        {/* Use case cards */}
        <div className="space-y-10">
          {useCases.map((uc, idx) => (
            <article
              key={uc.industry}
              className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center bg-white rounded-2xl border border-stone-100 overflow-hidden p-6 lg:p-0`}
            >
              {/* Image */}
              <div className="w-full lg:w-2/5 h-56 lg:h-80 flex-shrink-0 overflow-hidden rounded-xl lg:rounded-none">
                <img
                  src={uc.image}
                  alt={`AI ad generator for ${uc.industry}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="flex-1 lg:py-10 lg:px-8">
                <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-2 block">
                  {uc.tagline}
                </span>
                <h3 className="text-2xl font-black text-stone-900 mb-3 leading-snug">
                  {uc.industry}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed mb-5">{uc.description}</p>

                {/* Sample copy */}
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">
                    Sample AI-generated ad copy:
                  </p>
                  {uc.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-orange-500 text-sm" />
                      </div>
                      <span className="text-sm text-stone-600 italic">{b}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {uc.cta}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
